import { CustomerSelectHqOfficeModal } from "components/CustomerSelectHqOfficeModal";
import Sentry from "lib/sentry/init";
import { Spinner } from "@adfenix/alexandria";
import { selectHqOfficeModalState } from "atoms/selectedHqHoc";
import { ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSetRecoilState, useRecoilValue } from "recoil";
import useUserInfo from "hooks/useUserInfo";
import {
  getUserHqs,
  userSelectedHq,
  userSelectedOffice,
  getUserOffices as getUserofficesSelector
} from "atoms/auth";
import { getUserOffices, useHqAndDomains } from "api/services/configService";
import { getUser } from "api/services/authService";

export const HandleCustomerHqAndOfficeHoc = ({
  children
}: {
  children: ReactNode;
}) => {
  const { userEmail, accessLevel } = useUserInfo();
  const navigate = useNavigate();
  const setModalOpen = useSetRecoilState(selectHqOfficeModalState);
  const setUserSelectedHq = useSetRecoilState(userSelectedHq);
  const setUserSelectedOffice = useSetRecoilState(userSelectedOffice);
  const selectedHq = useRecoilValue(userSelectedHq);
  const selectedOffice = useRecoilValue(userSelectedOffice);
  const userHqs = useRecoilValue(getUserHqs);
  const userOffices = useRecoilValue(getUserofficesSelector);

  const location = useLocation();
  const { data: userInfo, status: userInfoStatus } = useQuery(
    ["getUser", userEmail],
    () => getUser(userEmail),
    {
      enabled: Boolean(userEmail)
    }
  );

  const { data: hqs, status } = useHqAndDomains(userEmail);

  const { data: offices, status: statusOffices } = useQuery(
    ["getUserOffices", userEmail],
    getUserOffices,
    { enabled: Boolean(userEmail) }
  );

  const shouldSelectHq = location.pathname.includes(":hqId");
  const shouldSelectOffice = location.pathname.includes(":officeId");

  useEffect(() => {
    if (
      (shouldSelectHq || shouldSelectOffice) &&
      status === "success" &&
      statusOffices === "success" &&
      userInfoStatus === "success"
    ) {
      const availableHqs = hqs?.filter(
        x =>
          userHqs.includes(x.id) ||
          userInfo?.offices.some(a => a.hq.id === x.id)
      );
      const availableOffices = offices?.filter(
        x =>
          userOffices.includes(x.officeId) ||
          userInfo?.offices.some(a => a.id === x.officeId)
      );

      if (availableHqs?.length === 1) {
        setModalOpen(false);
        setUserSelectedHq(availableHqs[0]);
      }

      if (availableOffices?.length === 1) {
        setModalOpen(false);
        setUserSelectedOffice(availableOffices[0]);
      }
    }
  }, [
    shouldSelectOffice,
    setModalOpen,
    shouldSelectHq,
    setUserSelectedHq,
    hqs,
    userHqs,
    userInfo,
    userOffices,
    offices,
    setUserSelectedOffice,
    status,
    statusOffices,
    userInfoStatus
  ]);

  useEffect(() => {
    if (
      (shouldSelectHq || shouldSelectOffice) &&
      status === "success" &&
      statusOffices === "success" &&
      userInfoStatus === "success"
    ) {
      if (shouldSelectHq && !selectedHq && !shouldSelectOffice) {
        setModalOpen(true);
      }

      if (shouldSelectHq && !shouldSelectOffice && selectedHq) {
        setModalOpen(false);
        navigate(location.pathname.replaceAll(":hqId", selectedHq.id));
      }

      if (
        shouldSelectHq &&
        shouldSelectOffice &&
        selectedHq &&
        selectedOffice
      ) {
        setModalOpen(false);
        navigate(
          location.pathname
            .replaceAll(":hqId", selectedHq.id)
            .replaceAll(":officeId", selectedOffice.officeId)
        );
      } else if (shouldSelectOffice) setModalOpen(true);
    }
  }, [
    shouldSelectOffice,
    setModalOpen,
    shouldSelectHq,
    selectedHq,
    location.pathname,
    navigate,
    selectedOffice,
    status,
    statusOffices,
    userInfoStatus
  ]);

  if (
    status === "error" ||
    statusOffices === "error" ||
    userInfoStatus === "error"
  ) {
    Sentry.captureException("Could not load hqs");
  }

  if (
    status === "loading" ||
    statusOffices === "loading" ||
    userInfoStatus === "loading"
  )
    return <Spinner centered />;

  return (
    <>
      <CustomerSelectHqOfficeModal
        hqs={
          accessLevel === "Multi-Brand"
            ? hqs
            : hqs?.filter(
                x =>
                  userHqs.includes(x.id) ||
                  userInfo?.offices.some(a => a.hq.id === x.id)
              )
        }
      />

      {!shouldSelectHq && !shouldSelectOffice && children}
    </>
  );
};
